// src/pages/mairie/PaiementContribuable.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';
import { useGeolocation } from '../../hooks/useGeolocation';

export default function PaiementContribuable() {
    const [telephone, setTelephone] = useState('');
    const [contribuable, setContribuable] = useState(null);
    const [contribuablesProches, setContribuablesProches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingListe, setLoadingListe] = useState(false);
    const [montant, setMontant] = useState('');
    const [typePaiement, setTypePaiement] = useState('journalier');
    const [commentaire, setCommentaire] = useState('');
    const [paiementEffectue, setPaiementEffectue] = useState(null);
    const [detteInfo, setDetteInfo] = useState(null);
    const [position, setPosition] = useState(null);

    const { getLocation } = useGeolocation();

    // Charger la position et la liste des contribuables proches au chargement
    useEffect(() => {
        const chargerPositionEtListe = async () => {
            try {
                const pos = await getLocation();
                if (pos) {
                    setPosition(pos);
                    await chargerContribuablesProches(pos.latitude, pos.longitude);
                }
            } catch (error) {
                console.error('Erreur position:', error);
            }
        };
        chargerPositionEtListe();
    }, []);

    const chargerContribuablesProches = async (lat, lng) => {
        setLoadingListe(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/contribuables_proches.php?lat=${lat}&lng=${lng}&rayon=5`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setContribuablesProches(data.contribuables);
            }
        } catch (error) {
            console.error('Erreur chargement liste:', error);
        } finally {
            setLoadingListe(false);
        }
    };

    const chercherContribuable = async () => {
        if (!telephone.trim()) {
            toast.error('Entrez un numéro de téléphone');
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/paiements_mairie.php?telephone=${telephone}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setContribuable(data.contribuable);
                setPaiementEffectue(null);
                setDetteInfo(null);
            } else {
                toast.error(data.message);
                setContribuable(null);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setLoading(false);
        }
    };

    const handlePaiement = async () => {
        if (!contribuable) {
            toast.error('Contribuable non trouvé');
            return;
        }
        if (!montant || parseFloat(montant) <= 0) {
            toast.error('Montant invalide');
            return;
        }

        if (typePaiement === 'journalier' && parseFloat(montant) % 100 !== 0) {
            toast.error('Le montant doit être un multiple de 100 F');
            return;
        }

        if (typePaiement === 'annuel' && contribuable.montant_annuel > 0) {
            if (parseFloat(montant) < contribuable.montant_annuel) {
                toast.error(`Le montant doit couvrir la taxe annuelle de ${contribuable.montant_annuel} F`);
                return;
            }
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/paiements_mairie.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    telephone: telephone,
                    montant: parseFloat(montant),
                    type_paiement: typePaiement,
                    commentaire: commentaire
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                setPaiementEffectue(data);
                setMontant('');
                chercherContribuable();
                // Rafraîchir la liste
                if (position) {
                    await chargerContribuablesProches(position.latitude, position.longitude);
                }
                if (data.dette_reglee > 0) {
                    setDetteInfo({
                        montant: data.dette_reglee,
                        jours: data.jours_non_acquittes
                    });
                }
            } else {
                toast.error(data.message);
                if (data.dette) {
                    setDetteInfo({
                        montant: data.dette,
                        jours: data.jours_non_acquittes
                    });
                }
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setLoading(false);
        }
    };

    const getProgression = () => {
        if (!contribuable) return 0;
        const solde = typePaiement === 'journalier' 
            ? contribuable.solde_journalier || 0 
            : contribuable.solde_annuel || 0;
        
        const max = typePaiement === 'journalier' ? 3000 : (contribuable.montant_annuel || 100000);
        return max > 0 ? Math.min((solde / max) * 100, 100) : 0;
    };

    const getJoursRestants = () => {
        if (!contribuable) return 0;
        const solde = typePaiement === 'journalier' 
            ? contribuable.solde_journalier || 0 
            : 0;
        return Math.floor(solde / 100);
    };

    const formaterDistance = (distance) => {
        if (distance < 1) {
            return `${Math.round(distance * 1000)} m`;
        }
        return `${distance.toFixed(1)} km`;
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">💰 Paiement</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Colonne de gauche : Liste des contribuables proches */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-lg p-4 sticky top-4">
                        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            📍 Proches de vous
                            {position && <span className="text-xs text-gray-400">({contribuablesProches.length})</span>}
                        </h2>
                        
                        {loadingListe ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
                            </div>
                        ) : contribuablesProches.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <p className="text-sm">Aucun contribuable proche</p>
                                <p className="text-xs mt-1">Déplacez-vous ou utilisez la recherche</p>
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                                {contribuablesProches.map((c) => (
                                    <div 
                                        key={c.id_contribuable}
                                        onClick={() => {
                                            setTelephone(c.telephone);
                                            chercherContribuable();
                                        }}
                                        className={`p-3 rounded-lg border cursor-pointer transition hover:shadow-md ${
                                            telephone === c.telephone && contribuable 
                                                ? 'border-blue-500 bg-blue-50' 
                                                : 'border-gray-200 hover:border-blue-300'
                                        }`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-800 truncate">
                                                    {c.prenom} {c.nom}
                                                </p>
                                                <p className="text-xs text-gray-500">📱 {c.telephone}</p>
                                                <p className="text-xs text-gray-400 truncate">
                                                    📍 {c.quartier || 'Non défini'}
                                                </p>
                                            </div>
                                            <div className="text-right flex-shrink-0 ml-2">
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                    {formaterDistance(c.distance)}
                                                </span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTelephone(c.telephone);
                                                        chercherContribuable();
                                                    }}
                                                    className="block mt-1 text-xs bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition w-full"
                                                >
                                                    💳 Compte
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Colonne de droite : Gestion du compte */}
                <div className="lg:col-span-2">
                    {/* Recherche par numéro */}
                    <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
                        <div className="flex gap-4">
                            <input
                                type="text"
                                placeholder="Numéro de téléphone du contribuable"
                                value={telephone}
                                onChange={(e) => setTelephone(e.target.value)}
                                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                onClick={chercherContribuable}
                                disabled={loading}
                                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                            >
                                {loading ? '...' : '🔍 Chercher'}
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                            💡 Vous pouvez aussi cliquer sur un contribuable de la liste à gauche
                        </p>
                    </div>

                    {/* Infos du contribuable */}
                    {contribuable && (
                        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500 mb-6">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-lg font-semibold">{contribuable.prenom} {contribuable.nom}</h2>
                                    <p className="text-sm text-gray-500">📱 {contribuable.telephone}</p>
                                    <p className="text-sm text-gray-500">🏷️ N° Fiche: {contribuable.numero_fiche}</p>
                                    <p className="text-sm text-gray-500">💰 Taxe annuelle: {contribuable.montant_annuel || 0} F</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-400">Quartier: {contribuable.quartier || 'Non défini'}</p>
                                    <p className="text-xs text-gray-400">Superficie: {contribuable.superficie || 0} m²</p>
                                    {contribuable.distance && (
                                        <p className="text-xs text-blue-600 mt-1">
                                            📍 Distance: {formaterDistance(contribuable.distance)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Barre de progression */}
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                <div className="flex justify-between text-sm mb-1">
                                    <span>Solde {typePaiement === 'journalier' ? 'journalier' : 'annuel'}</span>
                                    <span>
                                        {typePaiement === 'journalier' 
                                            ? (contribuable.solde_journalier || 0) + ' F' 
                                            : (contribuable.solde_annuel || 0) + ' F'
                                        }
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                                    <div 
                                        className="h-full transition-all duration-500 rounded-full"
                                        style={{
                                            width: `${getProgression()}%`,
                                            background: getProgression() > 70 ? 'linear-gradient(to right, #22c55e, #16a34a)' :
                                                        getProgression() > 30 ? 'linear-gradient(to right, #eab308, #f59e0b)' :
                                                        'linear-gradient(to right, #ef4444, #dc2626)'
                                        }}
                                    ></div>
                                </div>
                                {typePaiement === 'journalier' && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {getJoursRestants()} jours restants
                                    </p>
                                )}
                            </div>

                            {/* Dette */}
                            {detteInfo && detteInfo.montant > 0 && (
                                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                                    <p className="text-sm text-red-700 font-semibold">
                                        ⚠️ Dette de {detteInfo.montant} F ({detteInfo.jours} jours non acquittés)
                                    </p>
                                    <p className="text-xs text-red-600 mt-1">
                                        Vous devez régler cette dette avant de pouvoir recharger
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Formulaire de paiement */}
                    {contribuable && (
                        <div className="bg-white p-6 rounded-xl shadow-lg">
                            <h3 className="text-lg font-semibold mb-4">Effectuer un paiement</h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Type de paiement</label>
                                    <select
                                        value={typePaiement}
                                        onChange={(e) => setTypePaiement(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="journalier">📆 Journalier (100 F/jour)</option>
                                        <option value="annuel">📅 Taxe annuelle ({contribuable.montant_annuel || 0} F)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Montant (FCFA)
                                        {typePaiement === 'journalier' && <span className="text-xs text-gray-400 ml-2">(multiple de 100)</span>}
                                    </label>
                                    <input
                                        type="number"
                                        value={montant}
                                        onChange={(e) => setMontant(e.target.value)}
                                        placeholder={typePaiement === 'journalier' ? "Ex: 1000" : "Ex: 50000"}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                                    />
                                    {typePaiement === 'journalier' && montant && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            {Math.floor(parseFloat(montant) / 100)} jour(s) payé(s)
                                        </p>
                                    )}
                                    {typePaiement === 'annuel' && contribuable.montant_annuel > 0 && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            Taxe annuelle: {contribuable.montant_annuel} F
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Commentaire (optionnel)</label>
                                    <textarea
                                        value={commentaire}
                                        onChange={(e) => setCommentaire(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                                        rows="2"
                                        placeholder="Information supplémentaire..."
                                    />
                                </div>

                                <button
                                    onClick={handlePaiement}
                                    disabled={loading}
                                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50 font-semibold"
                                >
                                    {loading ? 'Traitement...' : '💳 Effectuer le paiement'}
                                </button>
                            </div>

                            {/* Résumé du paiement */}
                            {paiementEffectue && (
                                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                                    <h4 className="font-semibold text-green-800 mb-2">✅ Paiement effectué</h4>
                                    <div className="text-sm space-y-1">
                                        <p><span className="text-gray-600">Montant crédité:</span> {paiementEffectue.montant_credite} F</p>
                                        {paiementEffectue.dette_reglee > 0 && (
                                            <p><span className="text-gray-600">Dette réglée:</span> {paiementEffectue.dette_reglee} F</p>
                                        )}
                                        {paiementEffectue.debits_effectues > 0 && (
                                            <p><span className="text-gray-600">Débits effectués:</span> {paiementEffectue.debits_effectues}</p>
                                        )}
                                        <p><span className="text-gray-600">Solde final:</span> <strong>{paiementEffectue.solde_final} F</strong></p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}